import React, { useState } from "react";
import { AlarmClock, Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAlerts } from "@/context/AlertsContext";
import { useApp } from "@/context/AppContext";

export const AlarmDialog = ({ open, onOpenChange }) => {
  const { t } = useApp();
  const { alarms, addAlarm, removeAlarm, toggleAlarm } = useAlerts();
  const [time, setTime] = useState("08:00");
  const [label, setLabel] = useState("");

  const add = () => {
    if (!time) return;
    addAlarm(time, label.trim());
    setLabel("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="alarm-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlarmClock className="w-5 h-5 text-primary" /> {t("alarmTitle")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("alarmTitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                {t("time")}
              </label>
              <input
                type="time"
                data-testid="alarm-time-input"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-2 w-full h-11 bg-background border border-border rounded-xl px-3 outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <input
              data-testid="alarm-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t("alarmLabelPlaceholder")}
              className="flex-1 h-11 bg-background border border-border rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
            <button
              data-testid="alarm-add-btn"
              onClick={add}
              className="h-11 w-11 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center hover:brightness-110 active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="alarm-list">
            {alarms.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">{t("noAlarms")}</p>
            ) : (
              alarms.map((a) => (
                <div
                  key={a.id}
                  data-testid={`alarm-item-${a.id}`}
                  className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5"
                >
                  <div className="flex-1">
                    <div className="font-heading text-xl font-bold tabular-nums">{a.time}</div>
                    {a.label && <div className="text-xs text-muted-foreground">{a.label}</div>}
                  </div>
                  <Switch
                    data-testid={`alarm-toggle-${a.id}`}
                    checked={a.enabled}
                    onCheckedChange={() => toggleAlarm(a.id)}
                  />
                  <button
                    data-testid={`alarm-remove-${a.id}`}
                    onClick={() => removeAlarm(a.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
